import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";

import { getFolderInfo } from "../../../services/api/files";

import { FaFolder, FaFileImage, FaArrowUp, FaArrowDown } from "react-icons/fa";
import { ImFolderUpload } from "react-icons/im";

import Folder from "../drive/Folder";
import File from "../drive/File";
import { useFilesContext } from "../../../context/domain/files/FilesContext";
import { Button } from "../../../components/ui"; // Import the Button component
import { itemMatchesSearchQuery } from "../../../utils/common/searchFilter";

/**
 * Filters files to only picures.
 *
 * @param {Array} files - List of file objects.
 * @returns {Array} - Filtered list of file objects.
 */
const filterPhotoFiles = (files) => {
  const allowedExtensions = [
    "jpg",
    "jpeg",
    "png",
    "gif",
    "bmp",
    "tiff",
    "webp",
  ];
  return (
    files?.filter((file) =>
      allowedExtensions.includes(file.name.split(".").pop().toLowerCase())
    ) || []
  );
};

const PhotoExplorer = ({
  title,
  path,
  searchQuery,
  isGridView,
  handleFileClick,
  comments,
}) => {
  const { enqueueRequest, requestQueue, isProcessingQueue } =
    useFilesContext();
  const [folderData, setFolderData] = useState(null);
  const [currentPath, setCurrentPath] = useState(path);
  const [combinedItems, setCombinedItems] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    key: "name",
    direction: "asc",
  });
  const [visibleItemsCount, setVisibleItemsCount] = useState(10); // Initial limit for visible items
  const abortControllerRef = useRef(null); // Ref to hold the AbortController

  useEffect(() => {
    async function fetchFolderData() {
      try {
        const data = await enqueueRequest(
          () => getFolderInfo(path + "/091-Photos"),
          "high"
        );
        setCurrentPath(path + "/091-Photos");
        setFolderData(data);
      } catch (error) {
        console.error("Error fetching folder data:", error);
      }
    }
    fetchFolderData();
  }, [path, enqueueRequest]);

  const updateCombinedItems = useCallback(async () => {
    if (folderData) {
      const filteredFiles = filterPhotoFiles(folderData.files) || [];
      const items = [
        ...(folderData.folders?.map((folder, index) => ({
          ...folder,
          type: "folder",
          uniqueKey: `folder-${folder.id || index}`,
        })) || []),
        ...filteredFiles.map((file, index) => ({
          ...file,
          type: "file",
          uniqueKey: `file-${file.id || index}`,
        })),
      ];
      setCombinedItems(items);
    }
  }, [folderData]);

  useEffect(() => {
    updateCombinedItems();
  }, [folderData, updateCombinedItems]);

  const filteredItems = combinedItems.filter((item) =>
    itemMatchesSearchQuery(item, searchQuery, ["name"])
  );

  const sortedItems = [...filteredItems].sort((a, b) => {
    if (a.type !== b.type) return a.type === "folder" ? -1 : 1;
    if (a[sortConfig.key] < b[sortConfig.key])
      return sortConfig.direction === "asc" ? -1 : 1;
    if (a[sortConfig.key] > b[sortConfig.key])
      return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  const handleSort = (key) => {
    setSortConfig((prevConfig) => ({
      key,
      direction:
        prevConfig.key === key && prevConfig.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

  // This is seperate from the function in Drive.jsx as the photos explorer navigates
  // separately from the main drive explorer.
  const handleFolderClick = async (folderName) => {
    const newPath = `${path}/091-Photos/${folderName}`;
    try {
      const folderInfo = await enqueueRequest(
        () => getFolderInfo(newPath),
        "high"
      );
      setFolderData(folderInfo);
      setCurrentPath(newPath);
    } catch (error) {
      console.error("Failed to fetch folder info:", error);
    }
  };

  const handleUpClick = async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort(); // Abort ongoing operations in File components
    }
    abortControllerRef.current = new AbortController(); // Create a new AbortController

    try {
      setCombinedItems([]); // Immediately unmount all child components
      setVisibleItemsCount(10); // Reset visible items count
      const newPath = currentPath.slice(0, currentPath.lastIndexOf("/"));
      const folderInfo = await enqueueRequest(
        () =>
          getFolderInfo(newPath, {
            signal: abortControllerRef.current.signal,
          }),
        "high"
      );
      setFolderData(folderInfo);
      setCurrentPath(newPath);

      // Clear the queue after processing the getFolderInfo request
      requestQueue.current = [];
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Failed to fetch folder info:", error);
      }
    }
  };

  const handleShowMore = () => {
    if (visibleItemsCount === 10) {
      setVisibleItemsCount(40); // Show 30 more items
    } else {
      setVisibleItemsCount(combinedItems.length); // Show all items
    }
  };

  const visibleItems = sortedItems.slice(0, visibleItemsCount); // Limit items to display

  return (
    <div className="bg-white shadow-md rounded-lg m-auto mt-2 caret-transparent">
      <div
        className={`grid ${comments ? "grid-cols-3" : "grid-cols-1"} gap-4 p-2 px-3 rounded-t-lg border-b font-semibold text-gray-600`}
      >
        <button
          onClick={() => handleSort("name")}
          className={`${comments ? "col-span-2" : "col-span-1"} text-left truncate flex items-center space-x-2`}
        >
          <span>{title}</span>
          {sortConfig.key === "name" &&
            (sortConfig.direction === "asc" ? (
              <FaArrowUp className="text-xs text-gray-400" />
            ) : (
              <FaArrowDown className="text-xs text-gray-400" />
            ))}
        </button>
        {comments && !isGridView && (
          <button
            onClick={() => handleSort("comments")}
            className="text-left truncate col-span-1 flex items-center space-x-2"
          >
            <span>Comments</span>
            {sortConfig.key === "comments" &&
              (sortConfig.direction === "asc" ? (
                <FaArrowUp className="text-xs text-gray-400" />
              ) : (
                <FaArrowDown className="text-xs text-gray-400" />
              ))}
          </button>
        )}
      </div>
      <div
        className={
          isGridView
            ? "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 p-2"
            : "p-2"
        }
      >
        {!currentPath.endsWith("/091-Photos") && sortedItems.length > 0 && (
          <>
            {isGridView ? (
              <div
                key={"up-folder"}
                className="bg-white border rounded-lg p-4 shadow-sm hover:bg-gray-100 hover:cursor-pointer items-center justify-center"
                onClick={() => handleUpClick()}
              >
                <div className="flex flex-col items-center justify-center h-full">
                  <div className="mb-2">
                    <ImFolderUpload className="text-yellow-500 text-4xl" />
                  </div>
                  <div className="text-center text-sm font-semibold truncate">
                    Up Folder
                  </div>
                </div>
              </div>
            ) : (
              <div
                key={"up-folder"}
                className="grid md:grid-cols-8 grid-cols-4 gap-4 md:gap-8 p-1 border-b hover:bg-gray-200 hover:cursor-pointer"
                onClick={() => handleUpClick()}
              >
                <div className="flex items-center space-x-2 md:col-span-4 col-span-3 pl-2">
                  <ImFolderUpload className="text-yellow-500 min-w-6 text-2xl" />
                  <span className="font-semibold truncate pl-2">Up Folder</span>
                </div>
              </div>
            )}
          </>
        )}
        {visibleItems.length === 0 ? (
          <div className="text-left text-gray-500 pl-2 ">
            <i>{!isProcessingQueue.current && !folderData ? "Empty" : "Loading..."}</i>
          </div>
        ) : (
          visibleItems.map((item) =>
            item.type === "folder" ? (
              <Folder
                key={item.uniqueKey}
                name={item.name}
                icon={<FaFolder className="text-yellow-500 min-w-6" />}
                onClick={() => handleFolderClick(item.name)}
                isGridView={isGridView}
                comments={comments}
              />
            ) : (
              <File
                key={item.uniqueKey}
                name={item.name}
                icon={<FaFileImage className="text-blue-500 min-w-6" />}
                onClick={() => handleFileClick(item.path)}
                isGridView={isGridView}
                comments={comments ? "Placeholder comment" : null}
                path={item.path}
                entryId={item.entry_id}
                abortSignal={abortControllerRef.current?.signal} // Pass the signal to File
              />
            )
          )
        )}
      </div>
      {sortedItems.length > visibleItemsCount && (
        <div className="text-center p-2">
          <Button
            text={visibleItemsCount === 10 ? "Show More" : "Show All"}
            variant="secondary"
            onClick={handleShowMore}
            className="px-4 py-2"
          />
        </div>
      )}
    </div>
  );
};

export default PhotoExplorer;
